import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import BarcodeScanner from '../components/BarcodeScanner';
import {
  WheelsHeader,
  WheelsTable,
  WheelsCards,
  WheelModal
} from '../components/wheels';
import '../styles/Wheels.css';

// Generate Julian date lot number (DDDX format)
const generateLotNumber = () => {
  const now = new Date();
  const lastDigit = now.getFullYear().toString().slice(-1);
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  const dayStr = dayOfYear.toString().padStart(3, '0');
  return `${dayStr}${lastDigit}`;
};

// Convert date to Julian format
const dateToJulian = (dateStr) => {
  const date = new Date(dateStr);
  const lastDigit = date.getFullYear().toString().slice(-1);
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay);
  return `${dayOfYear.toString().padStart(3, '0')}${lastDigit}`;
};

const Wheels = () => {
  const navigate = useNavigate();
  const [wheels, setWheels] = useState([]);
  const [filteredWheels, setFilteredWheels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem('wheelsViewMode');
    if (saved) return saved;
    return window.innerWidth <= 1024 ? 'cards' : 'table';
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedWheel, setSelectedWheel] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCreatePalletConfirm, setShowCreatePalletConfirm] = useState(false);
  const [creatingPallet, setCreatingPallet] = useState(false);
  const [showMoveToExisting, setShowMoveToExisting] = useState(false);
  const [pallets, setPallets] = useState([]);
  const [palletSearch, setPalletSearch] = useState('');
  const [movingToPallet, setMovingToPallet] = useState(false);

  // Form state
  const [wheelItems, setWheelItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [itemSearch, setItemSearch] = useState('');
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [showItemScanner, setShowItemScanner] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Lot number input state
  const [lotNumber, setLotNumber] = useState(generateLotNumber());
  const [lotDate, setLotDate] = useState(new Date().toISOString().split('T')[0]);

  // Label state
  const [labelText, setLabelText] = useState('');
  const [labelColor, setLabelColor] = useState('');
  const [notes, setNotes] = useState('');
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    fetchAllItems();
    fetchCustomers();
    fetchPallets();
  }, []);

  useEffect(() => {
    fetchWheels();
  }, [statusFilter]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchWheels();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
      if (!e.target.closest('.item-search-container')) {
        setShowItemDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    let filtered = wheels;
    if (customerFilter !== 'all') {
      filtered = filtered.filter(w => w.customer_id === parseInt(customerFilter));
    }
    if (searchTerm) {
      filtered = filtered.filter(w =>
        w.wheel_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.lot_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        w.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (dateFilter) {
      filtered = filtered.filter(w => {
        const wheelDate = new Date(w.created_at).toISOString().split('T')[0];
        return wheelDate === dateFilter;
      });
    }
    setFilteredWheels(filtered);
  }, [customerFilter, searchTerm, dateFilter, wheels]);

  // API Functions
  const fetchWheels = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/wheels?status=${statusFilter}`);
      setWheels(response.data);
      setFilteredWheels(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load wheels');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllItems = async () => {
    try {
      const response = await api.get('/items');
      setAllItems(response.data);
    } catch (err) {
      console.error('Failed to load items');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers?status=active');
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to load customers');
    }
  };

  const fetchPallets = async () => {
    try {
      const response = await api.get('/pallets?status=active');
      setPallets(response.data);
    } catch (err) {
      console.error('Failed to load pallets');
    }
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchWheels();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  // Handler Functions
  const handleCreateWheel = () => {
    setWheelItems([]);
    setSelectedWheel(null);
    setSelectedCustomerId('');
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setModalMode('create');
    setItemSearch('');
    setSelectedItemId('');
    setShowItemDropdown(false);
    setLabelText('');
    setLabelColor('');
    setNotes('');
    setLotNumber(generateLotNumber());
    setIsFull(false);
    setShowModal(true);
  };

  const handleEditWheel = async (wheel) => {
    try {
      const response = await api.get(`/wheels/${wheel.id}`);
      setSelectedWheel(response.data);
      setSelectedCustomerId(response.data.customer_id || '');
      setWheelItems(response.data.items?.map(item => ({
        item_id: item.item_id,
        name: item.name,
        part_number: item.part_number,
        quantity: item.quantity,
        case_weight: item.case_weight,
        subtotal_weight: item.subtotal_weight,
        lot_number: item.lot_number || generateLotNumber()
      })) || []);
      setLabelText(response.data.label_text || '');
      setLabelColor(response.data.label_color || '');
      setNotes(response.data.notes || '');
      setIsFull(response.data.is_full || false);
      setModalMode('edit');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load wheel details');
    }
  };

  const handleViewWheel = async (wheel) => {
    try {
      const response = await api.get(`/wheels/${wheel.id}`);
      setSelectedWheel(response.data);
      setWheelItems(response.data.items?.map(item => ({
        item_id: item.item_id,
        name: item.name,
        part_number: item.part_number,
        quantity: item.quantity,
        case_weight: item.case_weight,
        subtotal_weight: item.subtotal_weight,
        lot_number: item.lot_number
      })) || []);
      setModalMode('view');
      setShowModal(true);
    } catch (err) {
      alert('Failed to load wheel details');
    }
  };

  const handleSwitchToEdit = () => {
    setSelectedCustomerId(selectedWheel.customer_id);
    setCustomerSearch(selectedWheel.customer_name || '');
    setLabelText(selectedWheel.label_text || '');
    setLabelColor(selectedWheel.label_color || '');
    setNotes(selectedWheel.notes || '');
    setIsFull(selectedWheel.is_full || false);
    setModalMode('edit');
  };

  // Item handlers
  const stripLeadingZeros = (str) => (str || '').replace(/^0+/, '') || '0';
  const searchNormalized = stripLeadingZeros(itemSearch.toLowerCase());

  const filteredItems = allItems.filter(item => {
    const text = [item.name, item.part_number].join(' ').toLowerCase();
    const barcode = (item.barcode || '').toLowerCase();
    const barcodeNorm = stripLeadingZeros(barcode);
    const qtyStr = item.qty_per_case ? String(item.qty_per_case) : '';
    const words = itemSearch.toLowerCase().trim().split(/\s+/);
    if (words.length === 1) {
      const w = words[0];
      return text.includes(w) || barcode.includes(w) || barcodeNorm.includes(stripLeadingZeros(w)) || qtyStr === w;
    }
    return words.every(word => text.includes(word) || qtyStr === word);
  });

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.city?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    customer.state?.toLowerCase().includes(customerSearch.toLowerCase())
  );

  const handleSelectItem = (item) => {
    setSelectedItemId(item.id);
    setItemSearch(`${item.part_number} - ${item.name}`);
    setShowItemDropdown(false);
  };

  const handleSelectCustomer = (customer) => {
    setSelectedCustomerId(customer.id);
    setCustomerSearch(`${customer.name} - ${customer.city}, ${customer.state}`);
    setShowCustomerDropdown(false);
  };

  const handleAddItem = () => {
    if (!selectedItemId || quantity < 1) return;
    if (!lotNumber || lotNumber.length !== 4) {
      alert('Please enter a valid lot number (4 digits: DDDX)');
      return;
    }
    const item = allItems.find(i => i.id === parseInt(selectedItemId));
    if (!item) return;

    const existingIndex = wheelItems.findIndex(wi => wi.item_id === item.id && wi.lot_number === lotNumber);
    if (existingIndex >= 0) {
      const updated = [...wheelItems];
      updated[existingIndex].quantity += quantity;
      updated[existingIndex].subtotal_weight =
        (parseFloat(item.case_weight) || 0) * updated[existingIndex].quantity;
      setWheelItems(updated);
    } else {
      setWheelItems([...wheelItems, {
        item_id: item.id,
        name: item.name,
        part_number: item.part_number,
        quantity: quantity,
        case_weight: item.case_weight,
        subtotal_weight: (parseFloat(item.case_weight) || 0) * quantity,
        lot_number: lotNumber
      }]);
    }
    setSelectedItemId('');
    setItemSearch('');
    setQuantity(1);
    setLotNumber(generateLotNumber());
    setLotDate(new Date().toISOString().split('T')[0]);
  };

  const handleRemoveItem = (index) => {
    const updated = [...wheelItems];
    updated.splice(index, 1);
    setWheelItems(updated);
  };

  const handleUpdateQuantity = (index, newQty) => {
    if (newQty !== '' && newQty < 1) return;
    const updated = [...wheelItems];
    updated[index].quantity = newQty;
    updated[index].subtotal_weight =
      (parseFloat(updated[index].case_weight) || 0) * (parseInt(newQty) || 0);
    setWheelItems(updated);
  };

  const calculateTotalWeight = () => {
    return wheelItems.reduce((sum, item) => sum + (parseFloat(item.subtotal_weight) || 0), 0);
  };

  const calculateTotalCases = () => {
    return wheelItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
  };

  // Save/Delete handlers
  const handleSaveWheel = async () => {
    if (wheelItems.length === 0) {
      alert('Please add at least one item to the wheel');
      return;
    }
    if (!lotNumber || lotNumber.length !== 4) {
      alert('Please enter a valid lot number (4 digits: DDDX)');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        customer_id: selectedCustomerId || null,
        lot_number: lotNumber,
        label_text: labelText || null,
        label_color: labelColor || null,
        is_full: isFull,
        notes: notes || null,
        items: wheelItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          subtotal_weight: item.subtotal_weight,
          lot_number: item.lot_number
        }))
      };

      if (modalMode === 'create') {
        const response = await api.post('/wheels', payload);
        const newWheelId = response.data.id;
        const wheelResponse = await api.get(`/wheels/${newWheelId}`);
        setSelectedWheel(wheelResponse.data);
        setWheelItems(wheelResponse.data.items || []);
        setModalMode('view');
        fetchWheels();
      } else {
        await api.put(`/wheels/${selectedWheel.id}`, payload);
        const wheelResponse = await api.get(`/wheels/${selectedWheel.id}`);
        setSelectedWheel(wheelResponse.data);
        setWheelItems(wheelResponse.data.items || []);
        setModalMode('view');
        fetchWheels();
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save wheel');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWheel = async () => {
    try {
      await api.delete(`/wheels/${selectedWheel.id}`);
      setShowDeleteConfirm(false);
      setShowModal(false);
      fetchWheels();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete wheel');
    }
  };

  const handleCreatePalletFromWheel = async () => {
    if (!selectedWheel || wheelItems.length === 0) return;
    setCreatingPallet(true);
    try {
      const payload = {
        customer_id: selectedWheel.customer_id || null,
        label_text: selectedWheel.label_text || null,
        label_color: selectedWheel.label_color || null,
        source_wheel_id: selectedWheel.id,
        items: wheelItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          lot_number: item.lot_number
        }))
      };
      const response = await api.post('/pallets', payload);
      const newPalletId = response.data.id;
      setShowCreatePalletConfirm(false);
      setShowModal(false);
      fetchWheels();
      fetchPallets();
      navigate(`/pallets?id=${newPalletId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to create pallet from wheel');
    } finally {
      setCreatingPallet(false);
    }
  };

  const handleMoveToExistingPallet = async (palletId) => {
    if (!selectedWheel || wheelItems.length === 0) return;
    setMovingToPallet(true);
    try {
      await api.post(`/pallets/${palletId}/add-from-wheel`, {
        wheel_id: selectedWheel.id,
        items: wheelItems.map(item => ({
          item_id: item.item_id,
          quantity: item.quantity,
          lot_number: item.lot_number
        }))
      });
      setShowMoveToExisting(false);
      setShowCreatePalletConfirm(false);
      setShowModal(false);
      setPalletSearch('');
      fetchWheels();
      fetchPallets();
      navigate(`/pallets?id=${palletId}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to move items to pallet');
    } finally {
      setMovingToPallet(false);
    }
  };

  // Modal/view handlers
  const closeModal = () => {
    setShowModal(false);
    setSelectedWheel(null);
    setWheelItems([]);
    setShowDeleteConfirm(false);
    setShowCreatePalletConfirm(false);
    setShowMoveToExisting(false);
    setPalletSearch('');
    setLabelText('');
    setLabelColor('');
    setNotes('');
    setIsFull(false);
  };

  const toggleViewMode = (mode) => {
    setViewMode(mode);
    localStorage.setItem('wheelsViewMode', mode);
  };

  // Scanner handlers
  const handleScan = () => setShowScanner(true);

  const handleBarcodeScan = (barcode) => {
    setSearchTerm(barcode);
    setShowScanner(false);
  };

  const handleItemBarcodeScan = (barcode) => {
    setShowItemScanner(false);
    const scannedLower = barcode.toLowerCase().trim();
    const scannedNormalized = stripLeadingZeros(scannedLower);

    const item = allItems.find(i => {
      const partLower = i.part_number?.toLowerCase() || '';
      const barcodeLower = i.barcode?.toLowerCase() || '';
      const barcodeNormalized = stripLeadingZeros(barcodeLower);

      // Match exact or with leading zeros stripped
      return partLower === scannedLower ||
             barcodeLower === scannedLower ||
             barcodeNormalized === scannedNormalized;
    });

    if (item) {
      setSelectedItemId(item.id);
      setItemSearch(`${item.part_number} - ${item.name}`);
      setShowItemDropdown(false);
    } else {
      // Put barcode in search field to show filtered results
      setItemSearch(barcode);
      setShowItemDropdown(true);
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="wheels-page">
        <div className="loading">Loading wheels...</div>
      </div>
    );
  }

  return (
    <div className="wheels-page">
      <WheelsHeader
        viewMode={viewMode}
        onToggleView={toggleViewMode}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onScan={handleScan}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        customerFilter={customerFilter}
        onCustomerFilterChange={setCustomerFilter}
        customers={customers}
        onCreateClick={handleCreateWheel}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        onRefresh={handleManualRefresh}
        isRefreshing={isRefreshing}
        autoRefresh={autoRefresh}
        onAutoRefreshChange={setAutoRefresh}
      />

      {error && <div className="error-message">{error}</div>}

      {filteredWheels.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🛒</div>
          <p className="empty-text">No wheels found</p>
          <p className="empty-subtext">Create your first wheel to get started</p>
        </div>
      ) : viewMode === 'table' ? (
        <WheelsTable
          wheels={filteredWheels}
          onViewWheel={handleViewWheel}
          onEditWheel={handleEditWheel}
          formatDate={formatDate}
        />
      ) : (
        <WheelsCards
          wheels={filteredWheels}
          onViewWheel={handleViewWheel}
          onEditWheel={handleEditWheel}
          formatDate={formatDate}
        />
      )}

      <WheelModal
        isOpen={showModal}
        onClose={closeModal}
        modalMode={modalMode}
        selectedWheel={selectedWheel}
        wheelItems={wheelItems}
        onRemoveItem={handleRemoveItem}
        onUpdateQuantity={handleUpdateQuantity}
        onAddItem={handleAddItem}
        itemSearch={itemSearch}
        setItemSearch={setItemSearch}
        showItemDropdown={showItemDropdown}
        setShowItemDropdown={setShowItemDropdown}
        filteredItems={filteredItems}
        onSelectItem={handleSelectItem}
        selectedItemId={selectedItemId}
        quantity={quantity}
        setQuantity={setQuantity}
        lotNumber={lotNumber}
        setLotNumber={setLotNumber}
        lotDate={lotDate}
        setLotDate={setLotDate}
        dateToJulian={dateToJulian}
        customerSearch={customerSearch}
        setCustomerSearch={setCustomerSearch}
        showCustomerDropdown={showCustomerDropdown}
        setShowCustomerDropdown={setShowCustomerDropdown}
        filteredCustomers={filteredCustomers}
        onSelectCustomer={handleSelectCustomer}
        selectedCustomerId={selectedCustomerId}
        labelText={labelText}
        setLabelText={setLabelText}
        labelColor={labelColor}
        setLabelColor={setLabelColor}
        isFull={isFull}
        setIsFull={setIsFull}
        calculateTotalWeight={calculateTotalWeight}
        calculateTotalCases={calculateTotalCases}
        saving={saving}
        onSaveWheel={handleSaveWheel}
        onSwitchToEdit={handleSwitchToEdit}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        onDeleteWheel={handleDeleteWheel}
        showCreatePalletConfirm={showCreatePalletConfirm}
        setShowCreatePalletConfirm={setShowCreatePalletConfirm}
        onCreatePalletFromWheel={handleCreatePalletFromWheel}
        creatingPallet={creatingPallet}
        showMoveToExisting={showMoveToExisting}
        setShowMoveToExisting={setShowMoveToExisting}
        pallets={pallets}
        palletSearch={palletSearch}
        setPalletSearch={setPalletSearch}
        onMoveToExistingPallet={handleMoveToExistingPallet}
        movingToPallet={movingToPallet}
        onOpenItemScanner={() => setShowItemScanner(true)}
      />

      <BarcodeScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScan={handleBarcodeScan}
        title="Scan Wheel Barcode"
      />

      <BarcodeScanner
        isOpen={showItemScanner}
        onClose={() => setShowItemScanner(false)}
        onScan={handleItemBarcodeScan}
        title="Scan Item Barcode"
      />
    </div>
  );
};

export default Wheels;
